export function getFlowDataSnapshot(deps = {}) {
    const { getMainFlowDataSnapshot = () => ({}) } = deps;
    return getMainFlowDataSnapshot();
}

export function buildFlowExtractionHelpers(deps = {}) {
    const {
        getNodeData,
        getNodeTypeFromData,
        state,
        findMotion,
        getAxisDistanceUnit,
        getAxisAccelUnit,
        getAxisSpeedUnit,
        findSubprocessById
    } = deps;
    return {
        getNodeData,
        getNodeTypeFromData,
        resolveMotionByNode: (node, data) => {
            const motionId = data.motionId || state?.nodeIdToMotionId?.[String(node.id)] || "";
            return motionId ? findMotion(motionId) : null;
        },
        findMotion,
        getAxisDistanceUnit,
        getAxisAccelUnit,
        getAxisSpeedUnit,
        getSubprocessMeta: (subprocessId) => findSubprocessById(subprocessId)
    };
}

export function buildFlowContext(deps = {}) {
    const {
        state,
        getActivePlcProfile = () => ({ vendorTarget: "", id: "", name: "", promptRules: "" }),
        getFlowDataSnapshot = () => ({}),
        buildFlowContextFromSources = () => ({}),
        buildFlowExtractionHelpers = () => ({}),
        prepareProjectForGeneration = (project) => project
    } = deps;
    const activeProfile = getActivePlcProfile();
    const project = prepareProjectForGeneration(state?.project);
    return buildFlowContextFromSources(
        {
            project,
            flowData: getFlowDataSnapshot(),
            dialect: activeProfile.vendorTarget || state?.chat?.dialect,
            plcProfile: activeProfile
        },
        buildFlowExtractionHelpers()
    );
}

export function getFlowContext(deps = {}) {
    const {
        buildFlowContext = () => ({}),
        validateFlowContextContract = () => true
    } = deps;
    const context = buildFlowContext();
    if (!validateFlowContextContract(context)) {
        throw new Error("FlowContext contract validation failed");
    }
    return context;
}

export function getGenerationIr(deps = {}) {
    const {
        buildGenerationIrFromContext = () => ({}),
        buildStGenerationContext = () => ({}),
        validateGenerationIrContract = () => true
    } = deps;
    const ir = buildGenerationIrFromContext(buildStGenerationContext());
    if (!validateGenerationIrContract(ir)) {
        throw new Error("GenerationIR contract validation failed.");
    }
    return ir;
}

export function getGenerationSnapshot(deps = {}) {
    const {
        getFlowContext = () => ({}),
        getGenerationIr = () => ({}),
        getActivePlcProfile = () => ({ vendorTarget: "", id: "", name: "", promptRules: "" }),
        state,
        getDialectMeta = () => ({ label: "" })
    } = deps;
    const flowContext = getFlowContext();
    const generationIr = getGenerationIr();
    const activeProfile = getActivePlcProfile();
    return {
        flowContext,
        generationIr,
        assistant: {
            mode: state?.chat?.mode,
            dialect: activeProfile.vendorTarget || state?.chat?.dialect,
            dialectLabel: activeProfile.name || getDialectMeta(state?.chat?.dialect).label,
            plcProfileId: activeProfile.id,
            plcProfileName: activeProfile.name,
            plcProfilePromptRules: activeProfile.promptRules || "",
            plcProfile: activeProfile
        }
    };
}

export function exportFlowContext(deps = {}) {
    const {
        getFlowContext = () => ({}),
        BlobCtor = Blob,
        URLApi = URL,
        doc = document,
        setChatStatus = () => {},
        consoleError = (...args) => console.error(...args)
    } = deps;
    try {
        const flowContext = getFlowContext();
        const blob = new BlobCtor([JSON.stringify(flowContext, null, 2)], { type: "application/json" });
        const url = URLApi.createObjectURL(blob);
        const anchor = doc.createElement("a");
        anchor.href = url;
        anchor.download = "flow-context.json";
        anchor.click();
        URLApi.revokeObjectURL(url);
        setChatStatus("FlowContext JSON を出力しました");
        return true;
    } catch (error) {
        setChatStatus("FlowContext の出力に失敗しました");
        consoleError(error);
        return false;
    }
}

export function exportGenerationIr(deps = {}) {
    const {
        getGenerationIr = () => ({}),
        BlobCtor = Blob,
        URLApi = URL,
        doc = document,
        setChatStatus = () => {},
        consoleError = (...args) => console.error(...args)
    } = deps;
    try {
        const generationIr = getGenerationIr();
        const blob = new BlobCtor([JSON.stringify(generationIr, null, 2)], { type: "application/json" });
        const url = URLApi.createObjectURL(blob);
        const anchor = doc.createElement("a");
        anchor.href = url;
        anchor.download = "generation-ir.json";
        anchor.click();
        URLApi.revokeObjectURL(url);
        setChatStatus("GenerationIR JSON を出力しました");
        return true;
    } catch (error) {
        setChatStatus("GenerationIR の出力に失敗しました");
        consoleError(error);
        return false;
    }
}

export async function getApiClientAccessToken(client) {
    if (!client || typeof client.getAccessToken !== "function") return null;
    try {
        return await client.getAccessToken();
    } catch {
        return null;
    }
}

export function buildGenerationApiRequestPayload(deps = {}) {
    const {
        facade,
        buildRawFlowIndexForApi = () => null,
        activeProfile = {},
        state,
        mode = "",
        userMessage = "",
        questionContext = "",
        currentFlowDraft = null
    } = deps;
    return {
        flowContext: facade.getFlowContext(),
        generationIr: facade.getGenerationIr(),
        rawFlowIndex: buildRawFlowIndexForApi(),
        vendor: activeProfile.vendorTarget || state?.chat?.dialect,
        mode,
        userInstruction: userMessage,
        ...(questionContext ? { questionContext } : {}),
        plcProfileId: activeProfile.id,
        plcProfileName: activeProfile.name,
        plcProfilePromptRules: activeProfile.promptRules || "",
        plcProfile: activeProfile,
        skillIds: Array.isArray(state?.project?.skillIds) ? state.project.skillIds : [],
        ...(currentFlowDraft ? { currentFlowDraft } : {})
    };
}
